import React, { useState, useEffect } from "react";
import axios from "axios";
import dayjs from 'dayjs';
import { apiUrl } from "../../utility/Connections";
import "./formEvaluation.css";
import { Input, Button, Typography, Space, Row, Col, Select, Tooltip, DatePicker, InputNumber, Tag, Radio, message, Form, Modal, Alert, Spin } from "antd";
import { CloseOutlined, DeleteOutlined, EditOutlined, EyeOutlined, MailOutlined, PlusOutlined, QuestionCircleOutlined, SyncOutlined, UploadOutlined } from "@ant-design/icons";
import FileUploaderAuto from "../fileUploader/fileUploaderAuto";
import LoadingOverlay from "../loadingOverlay/loadingOverlay";
import TableCompetenceAnswerGroupRelation from "../tableCompetenceAnswerGroupRelation/tableCompetenceAnswerGroupRelation";
import TableAttributeAnswerGroupRelation from "../tableAttributeAnswerGroupRelation/tableAttributeAnswerGroupRelation";
import TableCultureAttributeAnswerGroupRelation from "../tableCultureAttributeAnswerGroupRelation/tableCultureAttributeAnswerGroupRelation";
import TableRelationships from "../tableRelationships/tableRelationships";
import TableEvaluationQuestions from "../tableEvaluationQuestions/tableEvaluationQuestions";

const { Text, Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const colorsConfig = [
    { color: 'red', label: 'Rojo', minKey: 'evl_min_red_light', maxKey: 'evl_max_red_light' },
    { color: 'yellow', label: 'Amarillo', minKey: 'evl_min_yellow_light', maxKey: 'evl_max_yellow_light' },
    { color: 'green', label: 'Verde', minKey: 'evl_min_green_light', maxKey: 'evl_max_green_light' },
    { color: 'cyan', label: 'Azul', minKey: 'evl_min_blue_light', maxKey: 'evl_max_blue_light' },
];

const normalizeWorkbookText = (value, { stripDimensionPrefix = false } = {}) => {
    let normalized = `${value ?? ''}`
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();

    if (stripDimensionPrefix) {
        normalized = normalized.replace(/^d\s*\d+\s*[-.:)]?\s*/, '');
    }

    return normalized.replace(/\s+/g, ' ');
};

const normalizeRelationshipDocument = (value) => `${value ?? ''}`
    .trim()
    .replace(/\.0$/, '')
    .replace(/\D/g, '');

const normalizeRelationshipType = (value, evaluatedDocument, evaluatorDocument) => {
    if (evaluatedDocument && evaluatorDocument && evaluatedDocument === evaluatorDocument) {
        return 'AUTO';
    }

    const normalized = `${value ?? ''}`
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .trim()
        .replace(/\s+/g, ' ');

    if (['AUTOEVALUACION', 'AUTO EVALUACION', 'AUTO'].includes(normalized)) {
        return 'AUTO';
    }

    return normalized;
};

const normalizeRelationshipRecord = (relationship, employees, index) => {
    const evaluatedDocument = normalizeRelationshipDocument(relationship?.evr_evaluated);
    const evaluatorDocument = normalizeRelationshipDocument(relationship?.evr_evaluator);
    const relationshipType = normalizeRelationshipType(relationship?.evr_type, evaluatedDocument, evaluatorDocument);

    if (!evaluatedDocument || !evaluatorDocument || !relationshipType) {
        return null;
    }

    const evaluatedInfo = employees.find((employee) => String(employee.empl_dni) === evaluatedDocument);
    const evaluatorInfo = employees.find((employee) => String(employee.empl_dni) === evaluatorDocument);

    return {
        ...relationship,
        evr_id: relationship?.evr_id ?? null,
        evr_uid: relationship?.evr_uid || relationship?.evr_uuid || relationship?.evr_id || `${evaluatorDocument}_${evaluatedDocument}_${index}`,
        evr_evaluated: evaluatedDocument,
        evr_evaluated_fullname: relationship?.evr_evaluated_fullname || (evaluatedInfo ? `${evaluatedInfo.empl_first_name} ${evaluatedInfo.empl_last_name}` : 'Nombre no encontrado'),
        evr_evaluated_job_title: relationship?.evr_evaluated_job_title || evaluatedInfo?.empl_job_title_name || 'Cargo no encontrado',
        evr_evaluator: evaluatorDocument,
        evr_evaluator_fullname: relationship?.evr_evaluator_fullname || (evaluatorInfo ? `${evaluatorInfo.empl_first_name} ${evaluatorInfo.empl_last_name}` : 'Nombre no encontrado'),
        evr_evaluator_job_title: relationship?.evr_evaluator_job_title || evaluatorInfo?.empl_job_title_name || 'Cargo no encontrado',
        evr_type: relationshipType,
    };
};

const normalizeRelationships = (relationships = [], employees = []) => relationships
    .map((relationship, index) => normalizeRelationshipRecord(relationship, employees, index))
    .filter(Boolean);

const EMPTY_EVALUATION = {
    evl_id: null,
    evl_title: "",
    evl_welcome_message: "",
    evl_main_welcome_text: "",
    evl_introductory_text: "",
    evl_question_display_mode: "grouped",
    evl_questions_per_page: 10,
    evl_start_date: "",
    evl_finish_date: "",
    evl_email_message: "",
    evl_email_responsible: "",
    evl_email_responsible_job_title: "",
    evl_email_responsible_email: "",
    evl_final_message: "",
    evl_min_red_light: 0,
    evl_max_red_light: "",
    evl_min_yellow_light: "",
    evl_max_yellow_light: "",
    evl_min_green_light: "",
    evl_max_green_light: "",
    evl_min_blue_light: "",
    evl_max_blue_light: 100,
    evl_indicators_porcentage: 0,
    evl_competencies_porcentage: 0,
    evl_comments_option: 0,
    evl_answer_options: [],
    evl_relationships: [],
    evl_questions: [],
};

const FormEvaluation = ({ data: rawData, dataCompetencies: rawDataCompetencies, dataEvaluationAnswerGroups: rawDataEvaluationAnswerGroups, dataEmployees: rawDataEmployees, setData, relEvlCmpEag: rawRelEvlCmpEag, setRelEvlCmpEag, onSave, onCancel, showSwal, type, enableRelationships = false, showSelfcareConfig = false, moduleName }) => {
    const data = rawData ?? EMPTY_EVALUATION;
    const dataCompetencies = rawDataCompetencies ?? [];
    const dataEvaluationAnswerGroups = rawDataEvaluationAnswerGroups ?? [];
    const dataEmployees = rawDataEmployees ?? [];
    const relEvlCmpEag = rawRelEvlCmpEag ?? [];
    const [showLoading, setShowLoading] = useState(false);
    const [loadingText, setLoadingText] = useState(null);
    const [showTestEmailModal, setShowTestEmailModal] = useState(false);
    const [testEmailRecipient, setTestEmailRecipient] = useState("");
    const [testEmailRecipientName, setTestEmailRecipientName] = useState("");
    const [sendingTestEmail, setSendingTestEmail] = useState(false);
    const [loadingTestEmailPreview, setLoadingTestEmailPreview] = useState(false);
    const [testEmailPreviewHtml, setTestEmailPreviewHtml] = useState("");
    const [testEmailPreviewError, setTestEmailPreviewError] = useState("");
    const [testEmailPreviewLinkActive, setTestEmailPreviewLinkActive] = useState(false);
    const [fileCharged, setFileCharged] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileChargedWorkbook, setFileChargedWorkbook] = useState(false);
    const [selectedFileWorkbook, setSelectedFileWorkbook] = useState(null);
    const [fileChargedQuestions, setFileChargedQuestions] = useState(false);
    const [selectedFileQuestions, setSelectedFileQuestions] = useState(null);
    // const [relEvlCmpEag, setRelEvlCmpEag] = useState([]);
    const [isWeightValid, setIsWeightValid] = useState(true);

    //Agregar relaciones
    const [evaluated, setEvaluated] = useState("");
    const [evaluator, setEvaluator] = useState("");
    const [evrType, setEvrType] = useState("");
    const [editingRelationshipUid, setEditingRelationshipUid] = useState(null);

    const [evaluatedInfo, setEvaluatedInfo] = useState(null);
    const [evaluatorInfo, setEvaluatorInfo] = useState(null);

    const [tableTitle, setTableTitle] = useState("");
    const [autoQuestionTitle, setAutoQuestionTitle] = useState("");
    const [autoQuestionText1, setAutoQuestionText1] = useState("");
    const [autoQuestionText2, setAutoQuestionText2] = useState("");
    const isIashConfiguration = type === "selfcare" && showSelfcareConfig;
    const effectiveQuestionDisplayMode = `${data.evl_question_display_mode || (isIashConfiguration ? 'random' : 'grouped')}`;
    const usesRandomQuestionDisplay = effectiveQuestionDisplayMode === 'random';
    const selectedCommentsOption = `${data.evl_comments_option ?? '0'}`;

    //Agregar preguntas
    const [question, setQuestion] = useState("");
    const [questionText, setQuestionText] = useState("");
    const [massDeleteTarget, setMassDeleteTarget] = useState(null); // 'questions' | 'relationships' | null
    const [massDeleteConfirmCode, setMassDeleteConfirmCode] = useState("");
    const [relationshipsPageData, setRelationshipsPageData] = useState([]);
    const [relationshipsPagination, setRelationshipsPagination] = useState({ page: 1, page_size: 20, total: 0, total_pages: 0 });
    const [loadingRelationshipsPage, setLoadingRelationshipsPage] = useState(false);
    const showRelationshipsSection = type !== "culture" && (type !== "selfcare" || enableRelationships);
    const isAutoManualRelationship = (() => {
        const evaluatedDocument = normalizeRelationshipDocument(evaluated);
        const evaluatorDocument = normalizeRelationshipDocument(evaluator);
        return Boolean(evaluatedDocument && evaluatorDocument && evaluatedDocument === evaluatorDocument);
    })();

    useEffect(() => {
        if (isIashConfiguration && `${data.evl_comments_option ?? ''}` !== '0') {
            setData(prev => ({
                ...prev,
                evl_comments_option: 0,
            }));
        }
    }, [data.evl_comments_option, isIashConfiguration, setData]);

    useEffect(() => {
        if (!data.evl_question_display_mode) {
            setData(prev => ({
                ...prev,
                evl_question_display_mode: isIashConfiguration ? 'random' : 'grouped',
            }));
        }
    }, [data.evl_question_display_mode, isIashConfiguration, setData]);

    useEffect(() => {
        const updatedRelations = [...relEvlCmpEag];
        
        dataCompetencies.forEach((cmp) => {
            let exists
            if (type === "competencies") {
                exists = relEvlCmpEag.some(rel => rel.cmp_id === cmp.cmp_id);
            } else if (type === "leadership") {
                exists = relEvlCmpEag.some(rel => rel.cmp_id === cmp.lat_id);
            } else if (type === "culture") {
                exists = relEvlCmpEag.some(rel => rel.cmp_id === cmp.cat_id);
            } else if (type === "selfcare") {
                exists = relEvlCmpEag.some(rel => rel.cmp_id === cmp.sca_id);
            }
            
            if (!exists) {
                if (type === "competencies") {
                    updatedRelations.push({
                        cmp_id: cmp.cmp_id,
                        name: cmp.cmp_competence,
                        description: cmp.cmp_description,
                        eag_id: null,
                        weight: null,
                        evl_id: data.evl_id ?? null,
                    });
                } else if (type === "leadership") {
                    updatedRelations.push({
                        cmp_id: cmp.lat_id,
                        name: cmp.lat_attribute,
                        description: cmp.lat_description,
                        eag_id: null,
                        weight: null,
                        evl_id: data.evl_id ?? null,
                    });
                } else if (type === "culture") {
                    updatedRelations.push({
                        cmp_id: cmp.cat_id,
                        name: cmp.cat_attribute,
                        description: cmp.cat_description,
                        cat_type: cmp.cat_type,
                        eag_id: null,
                        weight: null,
                        evl_id: data.evl_id ?? null,
                    });
                } else if (type === "selfcare") {
                    updatedRelations.push({
                        cmp_id: cmp.sca_id,
                        name: cmp.sca_attribute,
                        description: cmp.sca_description,
                        eag_id: null,
                        weight: null,
                        evl_id: data.evl_id ?? null,
                    });
                }
            }
        });

        if (type === "competencies") {
            setTableTitle("Competencia")
            setAutoQuestionTitle("Configuración de competencias");
            setAutoQuestionText1("Asigne a continuación el peso porcentual que tendrá cada competencia en la evaluación. El peso porcentual de todas las competencias debe sumar 100%. En caso de que alguna competencia no corresponda a la evaluación de algún colaborador, se redistribuirá el peso porcentual de esa competencia entre las competencias restantes.");
            setAutoQuestionText2("Asigne además el grupo de respuesta que corresponde a cada competencia. El grupo de respuesta es el conjunto de respuestas que se le presentan al evaluador para calificar al evaluado.");
        } else if (type === "leadership") {
            setTableTitle("Atributo del líder")
            setAutoQuestionTitle("Configuración de atributos del líder");
            setAutoQuestionText1("Asigne a continuación el peso porcentual que tendrá cada atributo del líder en la evaluación. El peso porcentual de todos los atributos del líder debe sumar 100%. En caso de que algún atributo del líder no corresponda a la evaluación de algún colaborador, se redistribuirá el peso porcentual de esa atributo del líder entre los atributos del líder restantes.");
            setAutoQuestionText2("Asigne además el grupo de respuesta que corresponde a cada atributo del líder. El grupo de respuesta es el conjunto de respuestas que se le presentan al evaluador para calificar al evaluado.");
        } else if (type === "culture") {
            setTableTitle("Atributo cultural")
            setAutoQuestionTitle("Configuración de atributos culturales");
            setAutoQuestionText1("Asigne a continuación el peso porcentual que tendrá cada atributo cultural en la evaluación. El peso porcentual de todos los atributos culturales debe sumar 100%. En caso de que algún atributo cultural no corresponda a la evaluación de algún colaborador, se redistribuirá el peso porcentual de ese atributo cultural entre los atributos culturales restantes.");
            setAutoQuestionText2("Asigne además el grupo de respuesta que corresponde a cada atributo cultural. El grupo de respuesta es el conjunto de respuestas que se le presentan al evaluador para calificar al evaluado.");
        } else if (type === "selfcare") {
            const moduleNoun = moduleName || "autocuidado";
            setTableTitle(`Atributo de ${moduleNoun}`)
            setAutoQuestionTitle(`Configuración de atributos de ${moduleNoun}`);
            setAutoQuestionText1(`Asigne a continuación el peso porcentual que tendrá cada atributo de ${moduleNoun} en la evaluación. El peso porcentual de todos los atributos de ${moduleNoun} debe sumar 100%. En caso de que algún atributo de ${moduleNoun} no corresponda a la evaluación de algún colaborador, se redistribuirá el peso porcentual de ese atributo de ${moduleNoun} entre los atributos de ${moduleNoun} restantes.`);
            setAutoQuestionText2(`Asigne además el grupo de respuesta que corresponde a cada atributo de ${moduleNoun}. El grupo de respuesta es el conjunto de respuestas que se le presentan al evaluador para calificar al evaluado.`);
        }

        if (updatedRelations.length !== relEvlCmpEag.length) {
            setRelEvlCmpEag(updatedRelations);
        }
    }, [data.evl_id, dataCompetencies, moduleName, relEvlCmpEag, setRelEvlCmpEag, type]);

    useEffect(() => {
        if (isAutoManualRelationship) {
            setEvrType("AUTO");
        } else if (evrType === "AUTO") {
            setEvrType("");
        }
    }, [evrType, isAutoManualRelationship]);

    const handleChange = (e) => {
        const { name, value } = e.target
        setData(prev => ({
            ...prev,
            [name]: value
        }))
    }

    const formatDateForEmailPayload = (value) => {
        if (!value) return "";
        if (dayjs.isDayjs(value)) return value.format('YYYY-MM-DD');
        return `${value}`;
    };

    const buildTestEmailPayload = () => ({
        to_email: testEmailRecipient.trim(),
        recipient_name: testEmailRecipientName.trim(),
        evl_id: data.evl_id,
        evl_title: data.evl_title,
        evl_email_message: data.evl_email_message,
        evl_email_responsible: data.evl_email_responsible,
        evl_email_responsible_job_title: data.evl_email_responsible_job_title,
        evl_email_responsible_email: data.evl_email_responsible_email,
        evl_start_date: formatDateForEmailPayload(data.evl_start_date),
        evl_finish_date: formatDateForEmailPayload(data.evl_finish_date),
        root_url: window.location.origin,
    });

    const getEmailPreviewValidationError = () => {
        const requiredFields = [
            { key: "evl_title", label: "Título" },
            { key: "evl_email_message", label: "Mensaje de correo a enviar" },
            { key: "evl_email_responsible", label: "Responsable del correo" },
            { key: "evl_email_responsible_job_title", label: "Cargo del responsable" },
            { key: "evl_email_responsible_email", label: "Email del responsable" },
            { key: "evl_start_date", label: "Fecha de inicio" },
            { key: "evl_finish_date", label: "Fecha de finalización" },
        ];

        for (const field of requiredFields) {
            const value = data[field.key];
            if (value === null || value === undefined || value.toString().trim() === "") {
                return `El campo "${field.label}" es obligatorio para enviar el correo de prueba`;
            }
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(data.evl_email_responsible_email)) {
            return "El email del responsable no es válido";
        }

        if (!testEmailRecipient.trim() || !emailRegex.test(testEmailRecipient.trim())) {
            return "Debe ingresar un correo destino válido para la prueba";
        }

        if (!testEmailRecipientName.trim()) {
            return "Debe ingresar el nombre del destinatario de prueba";
        }

        return "";
    };

    const validateEmailPreviewData = () => {
        const validationError = getEmailPreviewValidationError();
        if (validationError) {
            message.error(validationError);
            return false;
        }

        return true;
    };

    const fetchTestEmailPreview = async ({ notifyOnError = false } = {}) => {
        const validationError = getEmailPreviewValidationError();

        if (validationError) {
            setTestEmailPreviewHtml("");
            setTestEmailPreviewLinkActive(false);
            setTestEmailPreviewError(validationError);
            if (notifyOnError) {
                message.error(validationError);
            }
            return false;
        }

        setLoadingTestEmailPreview(true);
        setTestEmailPreviewError("");

        const token = localStorage.getItem('access_token');
        const headers = {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        };

        try {
            const response = await axios.post(
                `${apiUrl}/emails/render-test-evaluation-email-preview/`,
                buildTestEmailPayload(),
                headers,
            );

            setTestEmailPreviewHtml(response.data?.html || "");
            setTestEmailPreviewLinkActive(Boolean(response.data?.link_active));
            setTestEmailPreviewError("");
            return true;
        } catch (error) {
            const backendError = error.response?.data?.error || "No fue posible generar la previsualización del correo.";
            setTestEmailPreviewHtml("");
            setTestEmailPreviewLinkActive(false);
            setTestEmailPreviewError(backendError);
            if (notifyOnError) {
                message.error(backendError);
            }
            return false;
        } finally {
            setLoadingTestEmailPreview(false);
        }
    };

    const handleOpenTestEmailModal = () => {
        if (!data.evl_email_responsible_email) {
            message.warning("Complete primero la configuración básica del correo electrónico.");
        }

        setTestEmailPreviewHtml("");
        setTestEmailPreviewError("");
        setTestEmailPreviewLinkActive(false);
        setShowTestEmailModal(true);
    };

    useEffect(() => {
        if (!showTestEmailModal) {
            setLoadingTestEmailPreview(false);
            setTestEmailPreviewHtml("");
            setTestEmailPreviewError("");
            setTestEmailPreviewLinkActive(false);
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            fetchTestEmailPreview();
        }, 300);

        return () => window.clearTimeout(timeoutId);
    }, [showTestEmailModal, testEmailRecipient, testEmailRecipientName, data.evl_id, data.evl_title, data.evl_email_message, data.evl_email_responsible, data.evl_email_responsible_job_title, data.evl_email_responsible_email, data.evl_start_date, data.evl_finish_date]);

    const handleSendTestEmailPreview = () => {
        if (!validateEmailPreviewData()) {
            return;
        }

        setSendingTestEmail(true);
        const token = localStorage.getItem('access_token');
        const headers = {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        };

        const payload = buildTestEmailPayload();

        axios.post(`${apiUrl}/emails/send-test-evaluation-email-preview/`, payload, headers)
            .then((response) => {
                const linkActive = response.data?.link_active;
                message.success(
                    linkActive
                        ? "Correo de prueba enviado correctamente. Incluye enlace activo."
                        : "Correo de prueba enviado correctamente. La evaluación aún no tiene enlace activo porque no ha sido guardada."
                );
                setShowTestEmailModal(false);
            })
            .catch((error) => {
                console.error('There was an error!', error);
                message.error(error.response?.data?.error || "No fue posible enviar el correo de prueba.");
            })
            .finally(() => {
                setSendingTestEmail(false);
            });
    };

    const handleOpenPreviewInNewTab = () => {
        if (!testEmailPreviewHtml) {
            message.warning("Genere primero una previsualización válida del correo.");
            return;
        }

        const previewBlob = new Blob([testEmailPreviewHtml], { type: 'text/html' });
        const previewUrl = window.URL.createObjectURL(previewBlob);
        const previewWindow = window.open(previewUrl, "_blank", "noopener,noreferrer");
        if (!previewWindow) {
            window.URL.revokeObjectURL(previewUrl);
            message.error("El navegador bloqueó la nueva pestaña. Habilite las ventanas emergentes e intente de nuevo.");
            return;
        }

        window.setTimeout(() => {
            window.URL.revokeObjectURL(previewUrl);
        }, 60000);
    };

    const handleChangeDate = (name, value) => {
        setData(prev => ({
            ...prev,
            [name]: value,  // value es un objeto dayjs
        }));
    };

    const handleChangeLights = (key, value, index, isMax) => {
        setData((prev) => {
            const updated = { ...prev, [key]: value };

            // Si es Max y no es el último
            if (isMax && index < colorsConfig.length - 1) {
                const nextMinKey = colorsConfig[index + 1].minKey;
                const nextMinValue = value + 0.1;

                updated[nextMinKey] = nextMinValue;
            }

            return updated;
        });
    };

    const handleChangeCommentsOption = (e) => {
        setData(prev => ({ ...prev, evl_comments_option: e.target.value }));
    }; 

    const totalIndicatorsCompetencies =
        (parseInt(data.evl_indicators_porcentage, 10) || 0) +
        (parseInt(data.evl_competencies_porcentage, 10) || 0);

    const handleChangeIndicatorsCompetencies = (name, value) => {
        // Solo permitir enteros positivos
        const parsedValue = parseInt(value, 10);
        if (!isNaN(parsedValue)) {
            setData(prev => ({
                ...prev,
                [name]: parsedValue
            }));
        } else if (value === null) {
            // Permitir limpiar el input
            setData(prev => ({
                ...prev,
                [name]: 0
            }));
        }
    };

    const handleFileUpload = (file) => {
        setSelectedFile(file[0]);
        setFileCharged(true);
    }

    const handleChangeFile = () => {
        setFileCharged(false)
    }

    const fetchRelationshipsPage = (page = 1, pageSize = 20) => {
        if (!data?.evl_id || !showRelationshipsSection) {
            return;
        }

        setLoadingRelationshipsPage(true);
        const token = localStorage.getItem('access_token');
        const headers = {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        };

        axios
            .get(`${apiUrl}/evaluation-relationships/get-evaluation-relationships/${data.evl_id}/?page=${page}&page_size=${pageSize}`, headers)
            .then((response) => {
                const pageRows = normalizeRelationships(response.data?.data || [], dataEmployees);
                const pagination = response.data?.pagination || {
                    page,
                    page_size: pageSize,
                    total: pageRows.length,
                    total_pages: pageRows.length > 0 ? 1 : 0,
                };

                setRelationshipsPageData(pageRows);
                setRelationshipsPagination(pagination);
            })
            .catch((error) => {
                console.error('There was an error!', error);
                message.error('No fue posible cargar las relaciones paginadas.');
            })
            .finally(() => {
                setLoadingRelationshipsPage(false);
            });
    };

    const ensureAllRelationshipsLoaded = async () => {
        if (!data?.evl_id || data?.evl_relationships_loaded !== false) {
            return true;
        }

        setShowLoading(true);
        setLoadingText('Cargando todas las relaciones para habilitar edición...');

        const token = localStorage.getItem('access_token');
        const headers = {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        };

        try {
            const allRelationships = [];
            const pageSize = 500;
            let page = 1;
            let totalPages = 1;

            while (page <= totalPages) {
                const response = await axios.get(
                    `${apiUrl}/evaluation-relationships/get-evaluation-relationships/${data.evl_id}/?page=${page}&page_size=${pageSize}`,
                    headers,
                );

                const rows = response.data?.data || [];
                const pagination = response.data?.pagination;
                allRelationships.push(...rows);
                totalPages = pagination?.total_pages || 1;
                page += 1;
            }

            const normalizedRows = normalizeRelationships(allRelationships, dataEmployees);
            setData((prev) => ({
                ...prev,
                evl_relationships: normalizedRows,
                evl_relationships_loaded: true,
            }));

            return true;
        } catch (error) {
            console.error('There was an error!', error);
            message.error('No fue posible cargar todas las relaciones para editar.');
            return false;
        } finally {
            setShowLoading(false);
        }
    };

    useEffect(() => {
        if (!showRelationshipsSection || !data?.evl_id || data?.evl_relationships_loaded !== false) {
            return;
        }

        fetchRelationshipsPage(1, relationshipsPagination.page_size || 20);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.evl_id, data?.evl_relationships_loaded, showRelationshipsSection]);

    const handleRelationships = (relationships) => {
        const fileData = normalizeRelationships(relationships, dataEmployees);
        setData(prev => ({
            ...prev,
            evl_relationships: fileData,
            evl_relationships_loaded: true,
        }));
    };

    const handleBlurSearch = (dni, setInfo) => {
        const employee = dataEmployees.find((emp) => String(emp.empl_dni) === String(dni));
        if (!employee) {
            message.error(`No se encontró el empleado con DNI: ${dni}`);
            setInfo(null);
        } else {
            setInfo({
                fullname: `${employee.empl_first_name} ${employee.empl_last_name}`,
                job_title: employee.empl_job_title_name,
            });
        }
    };

    const handleAddRelationship = async () => {
        const canEditRelationships = await ensureAllRelationshipsLoaded();
        if (!canEditRelationships) {
            return;
        }

        const relationships = data.evl_relationships || [];
        const evaluatedDocument = normalizeRelationshipDocument(evaluated);
        const evaluatorDocument = normalizeRelationshipDocument(evaluator);
        const relationshipType = normalizeRelationshipType(evrType, evaluatedDocument, evaluatorDocument);
        const editingIndex = editingRelationshipUid
            ? relationships.findIndex((rel) => (rel.evr_uid || rel.evr_id) === editingRelationshipUid)
            : -1;

        if (!evaluatedDocument || !evaluatorDocument || !relationshipType || !evaluatedInfo || !evaluatorInfo) {
            message.warning("Completa todos los campos correctamente antes de agregar.");
            return;
        }

        const duplicateIndex = relationships.findIndex(
            (rel) => normalizeRelationshipDocument(rel.evr_evaluated) === evaluatedDocument && normalizeRelationshipDocument(rel.evr_evaluator) === evaluatorDocument
        );

        if (duplicateIndex >= 0 && duplicateIndex !== editingIndex) {
            message.warning("Ya existe una relación con ese evaluado y evaluador.");
            return;
        }

        const newRelationship = normalizeRelationshipRecord({
            evr_id: editingIndex >= 0 ? relationships[editingIndex].evr_id : null,
            evr_uid: editingIndex >= 0 ? relationships[editingIndex].evr_uid : null,
            evr_evaluated: evaluatedDocument,
            evr_evaluated_fullname: evaluatedInfo.fullname,
            evr_evaluated_job_title: evaluatedInfo.job_title,
            evr_evaluator: evaluatorDocument,
            evr_evaluator_fullname: evaluatorInfo.fullname,
            evr_evaluator_job_title: evaluatorInfo.job_title,
            evr_type: relationshipType,
        }, dataEmployees, relationships.length);

        const updatedRelationships = [...relationships];
        if (editingIndex >= 0) {
            updatedRelationships[editingIndex] = newRelationship;
            message.success("Relación actualizada correctamente.");
        } else {
            updatedRelationships.push(newRelationship);
            message.success("Relación agregada correctamente.");
        }

        handleRelationships(updatedRelationships);

        // Limpiar inputs
        setEvaluated("");
        setEvaluator("");
        setEvrType("");
        setEvaluatedInfo(null);
        setEvaluatorInfo(null);
        setEditingRelationshipUid(null);
    };

    const handleEditRelationship = (relationship) => {
        setEditingRelationshipUid(relationship.evr_uid || relationship.evr_id);
        setEvaluated(String(relationship.evr_evaluated || ''));
        setEvaluator(String(relationship.evr_evaluator || ''));
        setEvrType(relationship.evr_type || '');
        setEvaluatedInfo({
            fullname: relationship.evr_evaluated_fullname,
            job_title: relationship.evr_evaluated_job_title,
        });
        setEvaluatorInfo({
            fullname: relationship.evr_evaluator_fullname,
            job_title: relationship.evr_evaluator_job_title,
        });
    };

    const handleCancelRelationshipEdit = () => {
        setEditingRelationshipUid(null);
        setEvaluated("");
        setEvaluator("");
        setEvrType("");
        setEvaluatedInfo(null);
        setEvaluatorInfo(null);
    };
    
    const handleDeleteRelationship = async (uid) => {
        const canEditRelationships = await ensureAllRelationshipsLoaded();
        if (!canEditRelationships) {
            return;
        }

        setData(prev => ({
            ...prev,
            evl_relationships: prev.evl_relationships.filter((item) => (item.evr_uid || item.evr_id) !== uid)
        }));
    };

    const handleReceSelectChange = (cmpId, value) => {
        setRelEvlCmpEag((prev) =>
            prev.map((rel) =>
                rel.cmp_id === cmpId ? { ...rel, eag_id: value } : rel
            )
        );
    };

    const handleReceWeightChange = (cmpId, value) => {
        setRelEvlCmpEag((prev) =>
            prev.map((rel) =>
                rel.cmp_id === cmpId ? { ...rel, weight: value } : rel
            )
        );
    };

    const handleWeightTotalChange = (total, isValid) => {
        setIsWeightValid(isValid);
    };

    const applyDimensionConfiguration = (dimensionRows = []) => {
        if (!Array.isArray(dimensionRows) || dimensionRows.length === 0) {
            return;
        }

        const updatedRelations = relEvlCmpEag.map((relation) => {
            const relationName = normalizeWorkbookText(relation.name, { stripDimensionPrefix: true });
            const matchedDimension = dimensionRows.find((dimension) => {
                const rawName = normalizeWorkbookText(dimension.name);
                const strippedName = normalizeWorkbookText(dimension.name, { stripDimensionPrefix: true });
                return relationName === rawName || relationName === strippedName;
            });

            if (!matchedDimension) {
                return relation;
            }

            const answerGroupTitle = normalizeWorkbookText(matchedDimension.answer_group_title);
            const matchedAnswerGroup = dataEvaluationAnswerGroups.find((group) => {
                const groupTitle = normalizeWorkbookText(group.eag_title);
                return answerGroupTitle && (groupTitle === answerGroupTitle || groupTitle.includes(answerGroupTitle) || answerGroupTitle.includes(groupTitle));
            });

            return {
                ...relation,
                weight: matchedDimension.weight ?? relation.weight,
                eag_id: matchedAnswerGroup?.eag_id ?? relation.eag_id,
            };
        });

        setRelEvlCmpEag(updatedRelations);
    };

    const mapQuestionAnswerGroups = (questionRows = []) => {
        if (!Array.isArray(questionRows) || questionRows.length === 0) {
            return questionRows;
        }

        return questionRows.map((questionRow) => {
            const answerGroupTitle = normalizeWorkbookText(questionRow?.evq_answer_group_title);
            if (!answerGroupTitle) {
                return {
                    ...questionRow,
                    evq_eag_id: questionRow?.evq_eag_id ?? null,
                };
            }

            const matchedAnswerGroup = dataEvaluationAnswerGroups.find((group) => {
                const groupTitle = normalizeWorkbookText(group?.eag_title);
                return groupTitle && (groupTitle === answerGroupTitle || groupTitle.includes(answerGroupTitle) || answerGroupTitle.includes(groupTitle));
            });

            return {
                ...questionRow,
                evq_eag_id: matchedAnswerGroup?.eag_id ?? questionRow?.evq_eag_id ?? null,
            };
        });
    };

    const uploadFile = async () => {
        setShowLoading(true);
        setLoadingText("Procesando archivo. Esto puede tardar un poco...");

        const formData = new FormData();
        formData.append("file", selectedFile);

        const token = localStorage.getItem('access_token');

        const headers = {
            headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${token}`
            },
        }

        axios
            .post(`${apiUrl}/files/process-relationships/`, formData, headers)
            .then(function (response) {
                const relationships = response.data.relationships
                handleRelationships(relationships);
                setFileCharged(false);
                setSelectedFile(null);
                setShowLoading(false);
            })
            .catch(function (error) {
                setShowLoading(false)
                showSwal(`Error: ${error}`, 0, "Error");
                console.error(`Error: ${error}`);
            });
    };

    const handleFileUploadWorkbook = (file) => {
        setSelectedFileWorkbook(file[0]);
        setFileChargedWorkbook(true);
    };

    const handleChangeFileWorkbook = () => {
        setFileChargedWorkbook(false);
        setSelectedFileWorkbook(null);
    };

    const uploadEvaluationWorkbook = async () => {
        setShowLoading(true);
        setLoadingText("Procesando archivo de configuración de evaluación...");

        const formData = new FormData();
        formData.append("file", selectedFileWorkbook);
        formData.append("type", type);

        const token = localStorage.getItem('access_token');
        const headers = {
            headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${token}`
            },
        };

        axios
            .post(`${apiUrl}/files/process-evaluation-workbook/`, formData, headers)
            .then(function (response) {
                const config = response.data.config || {};
                const questions = mapQuestionAnswerGroups(response.data.questions || []);
                const dimensions = response.data.dimensions || [];
                const relationships = response.data.relationships || [];

                setData(prev => ({
                    ...prev,
                    ...config,
                    evl_comments_option: isIashConfiguration ? 0 : (config.evl_comments_option ?? prev.evl_comments_option),
                    evl_questions: questions.length > 0 ? questions : prev.evl_questions,
                }));

                if (showRelationshipsSection && relationships.length > 0) {
                    handleRelationships(relationships);
                }

                if (dimensions.length > 0) {
                    applyDimensionConfiguration(dimensions);
                }

                setFileChargedWorkbook(false);
                setSelectedFileWorkbook(null);
                setShowLoading(false);
                message.success("Configuración de evaluación cargada");
            })
            .catch(function (error) {
                setShowLoading(false);
                showSwal(`Error: ${error}`, 0, "Error");
                console.error(`Error: ${error}`);
            });
    };

    const handleFileUploadQuestions = (file) => {
        setSelectedFileQuestions(file[0]);
        setFileChargedQuestions(true);
    }

    const handleChangeFileQuestions = () => {
        setFileChargedQuestions(false)
    }

    const uploadFileQuestions = async () => {
        setShowLoading(true);
        setLoadingText("Procesando archivo. Esto puede tardar un poco...");

        const formData = new FormData();
        formData.append("file", selectedFileQuestions);
        formData.append("type", type);

        const token = localStorage.getItem('access_token');

        const headers = {
            headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${token}`
            },
        }

        axios
            .post(`${apiUrl}/files/process-questions/`, formData, headers)
            .then(function (response) {
                const questions = mapQuestionAnswerGroups(response.data.questions || [])
                setFileChargedQuestions(false);
                setSelectedFileQuestions(null);
                setShowLoading(false);
                setData(prev => ({
                    ...prev,
                    evl_questions: [...(prev.evl_questions || []), ...questions],
                }));
            })
            .catch(function (error) {
                setShowLoading(false)
                showSwal(`Error: ${error}`, 0, "Error");
                console.error(`Error: ${error}`);
            });
    };

    const handleAddQuestion = () => {
        // if (!question.trim() || !questionText.trim()) {
        //     message.warning("Debe completar ambos campos");
        //     return;
        // }

        let newQuestion = {
            evq_title: "",
            evq_question: "",
            evq_eag_id: null,
        };

        if (type == "competencies" || type == "leadership") {
            newQuestion.evq_dimension = 0;
        }

        setData(prev => ({
            ...prev,
            evl_questions: [...(prev.evl_questions || []), newQuestion],
        }));

        // setQuestion("");
        // setQuestionText("");
        message.success("Pregunta agregada");
    };

    const handleQuestionChange = (index, field, value) => {
        setData(prev => {
            const updatedQuestions = [...(prev.evl_questions || [])];

            if (!updatedQuestions[index]) {
                console.warn("Índice de pregunta inválido:", index);
                return prev;
            }

            updatedQuestions[index] = {
                ...updatedQuestions[index],
                [field]: value,
            };

            return {
                ...prev,
                evl_questions: updatedQuestions,
            };
        });
    };

    const handleDeleteQuestion = (index) => {
        setData(prev => ({
            ...prev,
            evl_questions: prev.evl_questions.filter((_, i) => i !== index)
        }));
    };

    const massDeleteCode = "ELIMINAR";

    const openMassDeleteDialog = (target) => {
        setMassDeleteTarget(target);
        setMassDeleteConfirmCode("");
    };

    const closeMassDeleteDialog = () => {
        setMassDeleteTarget(null);
        setMassDeleteConfirmCode("");
    };

    const handleConfirmMassDelete = async () => {
        if (massDeleteConfirmCode !== massDeleteCode) {
            message.error(`Debe escribir "${massDeleteCode}" para confirmar`);
            return;
        }

        if (massDeleteTarget === 'questions') {
            const count = (data.evl_questions || []).length;
            setData(prev => ({ ...prev, evl_questions: [] }));
            message.success(`${count} pregunta(s) eliminada(s)`);
        } else if (massDeleteTarget === 'relationships') {
            const count = data.evl_relationships_loaded === false
                ? (relationshipsPagination.total || 0)
                : (data.evl_relationships || []).length;

            const canEditRelationships = await ensureAllRelationshipsLoaded();
            if (!canEditRelationships) {
                return;
            }

            setData(prev => ({ ...prev, evl_relationships: [], evl_relationships_loaded: true }));
            setEditingRelationshipUid(null);
            setEvaluated("");
            setEvaluator("");
            setEvrType("");
            setEvaluatedInfo(null);
            setEvaluatorInfo(null);
            message.success(`${count} relación(es) eliminada(s)`);
        }

        closeMassDeleteDialog();
    };

    const validateBeforeSave = () => {
        // Validar campos requeridos
        const requiredFields = [
            { key: "evl_title", label: "Título" },
            { key: "evl_welcome_message", label: "Mensaje de bienvenida a la encuesta" },
            { key: "evl_main_welcome_text", label: "Mensaje de bienvenida listado de encuestas" },
            { key: "evl_start_date", label: "Fecha de inicio" },
            { key: "evl_finish_date", label: "Fecha de finalización" },
            { key: "evl_email_message", label: "Mensaje de correo a enviar" },
            { key: "evl_email_responsible", label: "Responsable del correo" },
            { key: "evl_email_responsible_job_title", label: "Cargo del responsable" },
            { key: "evl_email_responsible_email", label: "Email del responsable" },
            { key: "evl_final_message", label: "Mensaje final" }
        ];

        if (!isIashConfiguration) {
            requiredFields.push({ key: "evl_comments_option", label: "Opción de comentarios" });
        }

        if (usesRandomQuestionDisplay) {
            requiredFields.push({ key: "evl_questions_per_page", label: "Preguntas por página" });
        }

        for (const field of requiredFields) {
            const value = data[field.key];
            if (value === null || value === undefined || value.toString().trim() === "") {
                message.error(`El campo "${field.label}" es obligatorio`);
                return false;
            }
        }

        // Validar fechas
        const startDate = new Date(data.evl_start_date);
        const finishDate = new Date(data.evl_finish_date);
        if (isNaN(startDate) || isNaN(finishDate)) {
            message.error("Las fechas deben ser válidas");
            return false;
        }
        if (startDate >= finishDate) {
            message.error("La fecha de inicio debe ser anterior a la fecha de finalización");
            return false;
        }

        // Validar email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(data.evl_email_responsible_email)) {
            message.error("El email del responsable no es válido");
            return false;
        }

        if (type === "competencies") {
            // Validar porcentajes
            const indicators = parseInt(data.evl_indicators_porcentage, 10);
            const competencies = parseInt(data.evl_competencies_porcentage, 10);
            const total = indicators + competencies;
    
            if (
                isNaN(indicators) || isNaN(competencies) ||
                indicators < 0 || competencies < 0 ||
                indicators > 100 || competencies > 100
            ) {
                message.error("Los porcentajes deben ser números entre 0 y 100");
                return false;
            }
    
            if (total !== 100) {
                message.error("La suma de porcentajes debe ser exactamente 100");
                return false;
            }
        }

        // Validar rangos de colores
        for (let i = 0; i < colorsConfig.length; i++) {
            const min = parseFloat(data[colorsConfig[i].minKey]);
            const max = parseFloat(data[colorsConfig[i].maxKey]);

            if (isNaN(min) || isNaN(max) || max <= min) {
                message.error(`Revisa los valores del color ${colorsConfig[i].label}`);
                return false;
            }
        }

        // Validar relaciones (solo cuando aplica)
        if (showRelationshipsSection) {
            if (data.evl_relationships_loaded === false && data.evl_id) {
                const totalRelationships = relationshipsPagination.total || data.evl_relationships_total || 0;
                if (totalRelationships <= 0) {
                    message.error("Debe haber al menos una relación evaluador-evaluado");
                    return false;
                }
            } else {
                if (!data.evl_relationships || data.evl_relationships.length === 0) {
                    message.error("Debe haber al menos una relación evaluador-evaluado");
                    return false;
                }

                for (let [i, rel] of data.evl_relationships.entries()) {
                    const evaluatedDocument = normalizeRelationshipDocument(rel.evr_evaluated);
                    const evaluatorDocument = normalizeRelationshipDocument(rel.evr_evaluator);
                    const relationshipType = normalizeRelationshipType(rel.evr_type, evaluatedDocument, evaluatorDocument);
                    if (
                        !evaluatedDocument ||
                        !evaluatorDocument ||
                        !relationshipType
                    ) {
                        message.error(`Todos los campos de la relación #${i + 1} deben estar completos`);
                        return false;
                    }

                    if (relationshipType === 'AUTO' && evaluatedDocument !== evaluatorDocument) {
                        message.error(`La relación #${i + 1} solo puede ser AUTO cuando evaluador y evaluado son la misma persona`);
                        return false;
                    }
                }

                const relationshipKeys = new Set();
                for (const rel of data.evl_relationships) {
                    const key = `${normalizeRelationshipDocument(rel.evr_evaluator)}_${normalizeRelationshipDocument(rel.evr_evaluated)}`;
                    if (relationshipKeys.has(key)) {
                        message.error("No puede haber relaciones duplicadas con el mismo evaluador y evaluado");
                        return false;
                    }
                    relationshipKeys.add(key);
                }
            }
        }

        // Validar competencias con evaluación asociada
        for (let [i, item] of relEvlCmpEag.entries()) {
            if (
                item.eag_id === null || item.eag_id === undefined || item.eag_id === "" ||
                item.weight === null || item.weight === undefined || item.weight === ""
            ) {
                message.error(`Debe completar 'Grupo de respuesta' y 'Peso' para la competencia #${i + 1}`);
                return false;
            }
        }

        if (isWeightValid === false) {
            message.error("El peso total de las competencias debe sumar 100%");
            return false;
        }

        return true; // Todo OK
    };

    
    return (
        <Space direction="vertical" size="middle" style={{ width: '100%', margin: '0 auto' }}>
            {
                showLoading && <LoadingOverlay text={loadingText} showProgress={false} progress={null} />
            }
            
            <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <Text strong type="secondary">Carga de configuración de evaluación</Text>
                </div>
                <Text style={{ display: 'block', marginBottom: 4 }}>
                    Puede cargar un archivo con las hojas Config Evaluación y, si aplica al módulo, Preguntas Adicionales y Relaciones para poblar automáticamente el formulario.
                </Text>
                <div className='display-center-row display-center-column align-full-center'>
                    <div style={{ width: "80%", backgroundColor: "white" }} className='display-center-row main-subcontainer'>
                        {
                            fileChargedWorkbook ? <div className='display-center-row display-center-column align-full-center uploader-container' >
                                <p className='filename-tag'>{selectedFileWorkbook.name}</p>
                                <Button style={{ width: 'auto' }} type="primary" danger onClick={handleChangeFileWorkbook} icon={<SyncOutlined />}>Cambiar archivo</Button>
                            </div> : <div className='display-center-row display-center-column uploader-container'>
                                <FileUploaderAuto mostrarData={handleFileUploadWorkbook} multi={false} openSwal={showSwal} uploadFile={true} tamanoArchivo={100} />
                            </div>
                        }
                    </div>
                    <Button style={{ width: 'auto', marginBottom: 25 }} type="primary" onClick={uploadEvaluationWorkbook} disabled={!fileChargedWorkbook} icon={<UploadOutlined />}>Cargar configuración</Button>
                </div>
            </div>

            <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <Text strong type="secondary">Nombre de la evaluación</Text>
                    <Tooltip title="Ej: Evaluación de competencias o Valoración de Competencias.">
                        <QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
                    </Tooltip>
                </div>
                <Input
                    name="evl_title"
                    value={data.evl_title || ''}
                    onChange={handleChange}
                    placeholder="Digite el nombre de la evaluación"
                />
            </div>
            
            <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <Text strong type="secondary">Mensaje de bienvenida</Text>
                    <Tooltip title="Ej: Le agradecemos calificar cada una de las afirmaciones. Su contribución es fundamental para el éxito del proceso.">
                        <QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
                    </Tooltip>
                </div>
                <Text style={{ display: 'block', marginBottom: 4 }}>
                    Digite el texto del mensaje de bienvenida a las encuestas que verán los colaboradores en el encabezado.
                </Text>
                <TextArea
                    name="evl_welcome_message"
                    autoSize={{ minRows: 2, maxRows: 5 }}
                    value={data.evl_welcome_message || ''}
                    onChange={handleChange}
                        placeholder="Digite el mensaje de bienvenida a las encuestas"
                    />
                </div>

                <div style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <Text strong type="secondary">Mensaje de bienvenida listado de encuestas</Text>
                        <Tooltip title="Ej: Bienvenido al panel de evaluaciones donde encontrará los enlaces disponibles.">
                            <QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
                        </Tooltip>
                    </div>
                    <Text style={{ display: 'block', marginBottom: 4 }}>
                        Digite el texto de bienvenida que veán los colaboradores en el panel donde se listan sus evaluaciones.
                    </Text>
                    <TextArea
                        name="evl_main_welcome_text"
                        autoSize={{ minRows: 2, maxRows: 5 }}
                        value={data.evl_main_welcome_text || ''}
                        onChange={handleChange}
                        placeholder="Digite el mensaje de bienvenida listado de encuestas"
                    />
                </div>

                <div style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <Text strong type="secondary">Formato de visualización de preguntas</Text>
                        <Tooltip title="Permite presentar la evaluación agrupada por dimensiones o en páginas con preguntas aleatorias.">
                            <QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
                        </Tooltip>
                    </div>
                    <Text style={{ display: 'block', marginBottom: 8 }}>
                        Seleccione cómo se crearán las preguntas cuando el evaluador abra el enlace de la evaluación.
                    </Text>
                    <Radio.Group
                        value={effectiveQuestionDisplayMode}
                        onChange={(e) => setData(prev => ({
                            ...prev,
                            evl_question_display_mode: e.target.value,
                            evl_questions_per_page: e.target.value === 'random' ? (prev.evl_questions_per_page ?? 10) : prev.evl_questions_per_page,
                        }))}
                    >
                        <Radio value="grouped">Agrupado por dimensiones</Radio>
                        <Radio value="random">Aleatorio por páginas</Radio>
                    </Radio.Group>
                </div>

                <div style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <Text strong type="secondary">Texto introductorio antes de la primera pregunta</Text>
                        <Tooltip title="Este texto se muestra antes del primer bloque de preguntas, sin importar el tipo de evaluación.">
                            <QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
                        </Tooltip>
                    </div>
                    <Text style={{ display: 'block', marginBottom: 4 }}>
                        Digite el texto de apoyo que se mostrará justo antes de la primera pregunta de la evaluación.
                    </Text>
                    <TextArea
                        name="evl_introductory_text"
                        autoSize={{ minRows: 2, maxRows: 5 }}
                        value={data.evl_introductory_text || ''}
                        onChange={handleChange}
                        placeholder="Digite el texto introductorio de la evaluación"
                    />
                </div>

                {usesRandomQuestionDisplay && (
                <div style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <Text strong type="secondary">Preguntas por página</Text>
                        <Tooltip title="Solo aplica cuando el formato de visualización es aleatorio por páginas.">
                            <QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
                        </Tooltip>
                    </div>
                    <Text style={{ display: 'block', marginBottom: 4 }}>
                        Defina cuántas preguntas deben mostrarse por página en la evaluación pública.
                    </Text>
                    <InputNumber
                        min={1}
                        max={100}
                        step={1}
                        value={data.evl_questions_per_page ?? 10}
                        onChange={(value) => setData(prev => ({ ...prev, evl_questions_per_page: value ?? 10 }))}
                        style={{ width: 180 }}
                    />
                </div>
                )}

                <div style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <Text strong type="secondary">Fecha de inicio y cierre del proceso</Text>
                </div>
                <Row gutter={12}>
                    <Col xs={24} sm={12}>
                        <DatePicker
                            style={{ width: '100%' }}
                            placeholder="Fecha de inicio"
                            format="DD/MM/YYYY"
                            // value={new Date(data.evl_start_date)}
                            value={data.evl_start_date ? dayjs(data.evl_start_date) : null}
                            onChange={(date) => handleChangeDate('evl_start_date', date)}
                        />
                    </Col>

                    <Col xs={24} sm={12}>
                        <DatePicker
                            style={{ width: '100%' }}
                            placeholder="Fecha de cierre"
                            format="DD/MM/YYYY"
                            value={data.evl_finish_date ? dayjs(data.evl_finish_date) : null}
                            // value={new Date(data.evl_finish_date)}
                            onChange={(date) => handleChangeDate('evl_finish_date', date)}
                        />
                    </Col>
                </Row>
            </div>
            
            <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <Text strong type="secondary">Mensaje al final de la encuesta</Text>
                    <Tooltip title="Ej: Muchas gracias por haber participado en la evaluación. Su percepción es muy importante para nosotros.">
                        <QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
                    </Tooltip>
                </div>
                <Text style={{ display: 'block', marginBottom: 4 }}>
                    Digite el texto para el mensaje que verán los colaboradores al finalizar de la encuesta.
                </Text>
                <TextArea
                    name="evl_final_message"
                    autoSize={{ minRows: 2, maxRows: 5 }}
                    value={data.evl_final_message || ''}
                    onChange={handleChange}
                    placeholder="Digite el mensaje de bienvenida"
                />
            </div>

            <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <Text strong type="secondary">Mensaje para correo electrónico</Text>
                    {/* <Tooltip title="Ej: Muchas gracias por haber participado en la evaluación. Su percepción es muy importante para nosotros.">
                        <QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
                    </Tooltip> */}
                </div>
                <Text style={{ display: 'block', marginBottom: 4 }}>
                    Digite el texto para el mensaje que verán los colaboradores al recibir el correo electrónico para acceder a los enlaces de evaluación.
                </Text>
                <TextArea
                    name="evl_email_message"
                    autoSize={{ minRows: 2, maxRows: 5 }}
                    value={data.evl_email_message || ''}
                    onChange={handleChange}
                    placeholder="Digite el mensaje del correo electrónico"
                />
            </div>

            <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <Text strong type="secondary">Información para envío de correos electrónicos</Text>
                    {/* <Tooltip title="Ej: Muchas gracias por haber participado en la evaluación. Su percepción es muy importante para nosotros.">
                        <QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
                    </Tooltip> */}
                </div>
                <Text style={{ display: 'block', marginBottom: 4 }}>
                    Digite los datos del responsable de enviar los correos electrónicos. Esta información se usará para enviar los correos electrónicos a los evaluadores y evaluados.
                </Text>
                <Input
                    name="evl_email_responsible"
                    value={data.evl_email_responsible}
                    onChange={handleChange}
                    placeholder="Nombre del responsable"
                    style={{ marginBottom: 8 }}
                />
                <Input
                    name="evl_email_responsible_job_title"
                    value={data.evl_email_responsible_job_title}
                    onChange={handleChange}
                    placeholder="Cargo del responsable"
                    style={{ marginBottom: 8 }}
                />
                <Input
                    name="evl_email_responsible_email"
                    value={data.evl_email_responsible_email}
                    onChange={handleChange}
                    placeholder="Correo electrónico del responsable"
                />
                <div style={{ marginTop: 16 }}>
                    <Button
                        onClick={handleOpenTestEmailModal}
                        icon={<MailOutlined />}
                        size="large"
                        className="custom-purple-button form-evaluation-email-test-button"
                    >
                        Enviar correo de prueba
                    </Button>
                </div>
            </div>
            
            <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <Text strong type="secondary">Configuración del sermáforo</Text>
                    <Tooltip title="Ej: Rojo: 0%-60%, Amarillo: 60.1%-75%, Verde: 75.1%-90%, Azul: 90.1%-100%">
                        <QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
                    </Tooltip>
                </div>
                <Text style={{ display: 'block', marginBottom: 4 }}>
                    Configure a continuación los rangos de cada color del semáforo. El rango de cada color debe ser diferente y no puede haber rangos vacíos. El rango de cada color debe ser menor al rango del siguiente color.
                </Text>
                <>
                    {colorsConfig.map(({ color, label, minKey, maxKey }, index) => (
                        <Row key={color} align="middle" gutter={8} style={{ marginBottom: 12 }}>
                            <Col>
                                <Tag color={color} style={{ width: 100, padding: '4px 12px', fontWeight: 'bold' }}>
                                    {label}
                                </Tag>
                            </Col>
                            <Col>
                                <Text strong>Min:</Text>
                                <InputNumber
                                    value={data[minKey]}
                                    readOnly
                                    style={{ width: 100, marginLeft: 4 }}
                                />
                            </Col>
                            <Col>
                                <Text strong>Max:</Text>
                                <InputNumber
                                    value={data[maxKey]}
                                    onChange={(value) => handleChangeLights(maxKey, value, index, true)}
                                    min={parseFloat((data[color.minKey] + 0.1).toFixed(1))}
                                    max={100}
                                    step={0.1}
                                    style={{ width: 100, marginLeft: 4 }}
                                    readOnly={index === colorsConfig.length - 1} // Solo el último campo es editable
                                />
                            </Col>
                        </Row>
                    ))}
                </>
            </div>

            
            <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <Text strong type="secondary">
                        {autoQuestionTitle}
                    </Text>
                </div>
                <Text style={{ display: 'block', marginBottom: 4 }}>
                    {autoQuestionText1}
                </Text>
                <Text style={{ display: 'block', marginBottom: 4 }}>
                    {autoQuestionText2}
                </Text>
                {
                    type === "competencies" && <TableCompetenceAnswerGroupRelation relEvlCmpEag={relEvlCmpEag} dataEvaluationAnswerGroups={dataEvaluationAnswerGroups} handleSelectChange={handleReceSelectChange} handleWeightChange={handleReceWeightChange} onWeightTotalChange={handleWeightTotalChange} title={tableTitle} />
                }
                {
                    type === "leadership" && <TableAttributeAnswerGroupRelation relEvlCmpEag={relEvlCmpEag} dataEvaluationAnswerGroups={dataEvaluationAnswerGroups} handleSelectChange={handleReceSelectChange} handleWeightChange={handleReceWeightChange} onWeightTotalChange={handleWeightTotalChange} title={tableTitle} />
                }
                {
                    type === "selfcare" && <TableAttributeAnswerGroupRelation relEvlCmpEag={relEvlCmpEag} dataEvaluationAnswerGroups={dataEvaluationAnswerGroups} handleSelectChange={handleReceSelectChange} handleWeightChange={handleReceWeightChange} onWeightTotalChange={handleWeightTotalChange} title={tableTitle} />
                }
                {
                    type === "culture" && <TableCultureAttributeAnswerGroupRelation relEvlCmpEag={relEvlCmpEag} dataEvaluationAnswerGroups={dataEvaluationAnswerGroups} handleSelectChange={handleReceSelectChange} handleWeightChange={handleReceWeightChange} onWeightTotalChange={handleWeightTotalChange} title={tableTitle} />
                }
                
            </div>
            
            {type === "competencies" && (
            <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <Text strong type="secondary">Peso de competencias vs indicadores</Text>
                </div>
                <Text style={{ display: 'block', marginBottom: 4 }}>
                    Aisgne a continuación el peso porcentual que tendrá esta evaluación de competencias frente a la evaluación de indicadores. El peso porcentual de ambas evaluaciones debe sumar 100%.
                </Text>
                <br></br>
                <Row gutter={[24, 16]}>
                    <Col>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <label style={{ marginBottom: 4 }}>Indicadores (%)</label>
                            <InputNumber
                                min={0}
                                max={100}
                                step={1}
                                value={data.evl_indicators_porcentage}
                                onChange={(value) =>
                                    handleChangeIndicatorsCompetencies("evl_indicators_porcentage", value)
                                }
                            />
                        </div>
                    </Col>

                    <Col>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <label style={{ marginBottom: 4 }}>Competencias (%)</label>
                            <InputNumber
                                min={0}
                                max={100}
                                step={1}
                                value={data.evl_competencies_porcentage}
                                onChange={(value) =>
                                    handleChangeIndicatorsCompetencies("evl_competencies_porcentage", value)
                                }
                            />
                        </div>
                    </Col>

                    <Col>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <label style={{ marginBottom: 4 }}>Total</label>
                            <Tag
                                color={totalIndicatorsCompetencies === 100 ? "green" : "red"}
                                style={{
                                    width: 60,
                                    height: 32,
                                    lineHeight: "32px",
                                    textAlign: "center",
                                    padding: 0,
                                }}
                            >
                                {totalIndicatorsCompetencies}%
                            </Tag>
                        </div>
                    </Col>
                </Row>
            </div>
            )}

            {!isIashConfiguration && <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <Text strong type="secondary">Comentarios para el evaluado del evaluador</Text>
                </div>
                <Text style={{ display: 'block', marginBottom: 4 }}>
                    Elija la opción que desee configurar para agregar campos de comentarios para el evaluador.
                </Text>
                <Radio.Group
                    onChange={handleChangeCommentsOption}
                    value={selectedCommentsOption}
                    style={{ display: 'flex', flexDirection: 'column', marginTop: 8 }}
                >
                    <Radio value={'0'}>No incluir comentarios</Radio>
                    <Radio value={'1'}>Por comportamiento</Radio>
                    <Radio value={'2'}>Por competencia</Radio>
                    <Radio value={'3'}>Al final de la evaluación</Radio>
                </Radio.Group>
            </div>}
            
            {showRelationshipsSection && (
            <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <Text strong type="secondary">Relaciones evaluador-evaluado</Text>
                    {/* <Tooltip title="Ej: Muchas gracias por haber participado en la evaluación. Su percepción es muy importante para nosotros.">
                        <QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
                    </Tooltip> */}
                </div>
                <Text style={{ display: 'block', marginBottom: 4 }}>
                    A continuación puede realizar la carga de las relaciones Evaluador - Evaluado. Puede usar el cargador de archivo para cargar las relaciones arrastrando el archivo o dando click en el cargador y luego dando click en "Cargar relaciones".
                </Text>
                <div className='display-center-row display-center-column align-full-center'>
                    <div style={{ width: "80%", backgroundColor: "white" }} className='display-center-row main-subcontainer'>
                        {
                            fileCharged ? <div className='display-center-row display-center-column align-full-center uploader-container' >
                                <p className='filename-tag'>{selectedFile.name}</p>
                                <Button style={{ width: 'auto' }} type="primary" danger onClick={handleChangeFile} icon={<SyncOutlined />}>Cambiar archivo</Button>
                            </div> : <div className='display-center-row display-center-column uploader-container'>
                                <FileUploaderAuto mostrarData={handleFileUpload} multi={false} openSwal={showSwal} uploadFile={true} tamanoArchivo={100} />
                            </div>
                        }
                    </div>
                    <Button style={{ width: 'auto', marginBottom: 25 }} type="primary" onClick={uploadFile} disabled={!fileCharged} icon={<UploadOutlined />}>Cargar relaciones</Button>
                    <Text style={{ width: '100%', display: 'block', marginBottom: 25 }}>
                        Si desea cargar las relaciones manualmente, complete los campos y haga clic en el botón "Agregar relación".
                    </Text>
                    <Form layout="vertical" style={{ width: "100%" }}>
                        <Row gutter={16}>
                            {/* Columna del evaluado */}
                            <Col span={8}>
                                <Form.Item label="Evaluado (DNI)">
                                    <Input
                                        value={evaluated}
                                        onChange={(e) => setEvaluated(e.target.value)}
                                        onBlur={() => handleBlurSearch(evaluated, setEvaluatedInfo)}
                                        placeholder="DNI del evaluado"
                                    />
                                </Form.Item>
                                <Form.Item label="Nombre del Evaluado">
                                    <Input value={evaluatedInfo?.fullname || ""} readOnly />
                                </Form.Item>
                                <Form.Item label="Cargo del Evaluado">
                                    <Input value={evaluatedInfo?.job_title || ""} readOnly />
                                </Form.Item>
                            </Col>

                            {/* Columna del evaluador */}
                            <Col span={8}>
                                <Form.Item label="Evaluador (DNI)">
                                    <Input
                                        value={evaluator}
                                        onChange={(e) => setEvaluator(e.target.value)}
                                        onBlur={() => handleBlurSearch(evaluator, setEvaluatorInfo)}
                                        placeholder="DNI del evaluador"
                                    />
                                </Form.Item>
                                <Form.Item label="Nombre del Evaluador">
                                    <Input value={evaluatorInfo?.fullname || ""} readOnly />
                                </Form.Item>
                                <Form.Item label="Cargo del Evaluador">
                                    <Input value={evaluatorInfo?.job_title || ""} readOnly />
                                </Form.Item>
                            </Col>

                            {/* Columna del tipo de relación y botón */}
                            <Col span={8}>
                                <Form.Item label="Tipo de relación">
                                    <Input
                                        value={evrType}
                                        onChange={(e) => setEvrType(normalizeRelationshipType(e.target.value, normalizeRelationshipDocument(evaluated), normalizeRelationshipDocument(evaluator)))}
                                        placeholder="Ej: JEFE, PAR, AUTO"
                                        disabled={isAutoManualRelationship}
                                    />
                                </Form.Item>
                                <Form.Item label=" ">
                                    <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
                                        <Button
                                            type="primary"
                                            onClick={handleAddRelationship}
                                            block
                                            icon={editingRelationshipUid ? <EditOutlined /> : <PlusOutlined />}
                                        >
                                            {editingRelationshipUid ? 'Actualizar relación' : 'Agregar relación'}
                                        </Button>
                                        {editingRelationshipUid && (
                                            <Button
                                                onClick={handleCancelRelationshipEdit}
                                                block
                                                icon={<CloseOutlined />}
                                            >
                                                Cancelar edición
                                            </Button>
                                        )}
                                    </Space>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                    {
                        ((data.evl_relationships_loaded === false && relationshipsPagination.total > 0) || (data["evl_relationships"] && data.evl_relationships.length > 0)) && <>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                                <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => openMassDeleteDialog('relationships')}
                                >
                                    Eliminar todas las relaciones ({data.evl_relationships_loaded === false ? (relationshipsPagination.total || 0) : data.evl_relationships.length})
                                </Button>
                            </div>
                            <TableRelationships
                                isLinks={false}
                                evl_relationships={data.evl_relationships_loaded === false ? relationshipsPageData : data.evl_relationships}
                                handleDeleteRelationship={handleDeleteRelationship}
                                handleEditRelationship={handleEditRelationship}
                                loading={data.evl_relationships_loaded === false ? loadingRelationshipsPage : false}
                                serverPagination={data.evl_relationships_loaded === false}
                                pagination={relationshipsPagination}
                                onPageChange={(page, pageSize) => fetchRelationshipsPage(page, pageSize)}
                                evl_id={data.evl_id}
                            />
                        </>
                    }

                </div>
            </div>
            )}

            <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <Text strong type="secondary">Preguntas adicionales</Text>
                    {/* <Tooltip title="Ej: Muchas gracias por haber participado en la evaluación. Su percepción es muy importante para nosotros.">
                        <QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
                    </Tooltip> */}
                </div>
                <Text style={{ display: 'block', marginBottom: 4 }}>
                    A continuación, puede agregar preguntas adicionales que desea que el evaluador responda al finalizar la evaluación.
                </Text>
                <Text style={{ display: 'block', marginBottom: 4 }}>
                    Puede hacerlo a través del cargador de archivos que encontrará a continuación, o bien, puede agregar preguntas manualmente.
                </Text>
                <div className='display-center-row display-center-column align-full-center'>
                    <div style={{ width: "80%", backgroundColor: "white" }} className='display-center-row main-subcontainer'>
                        {
                            fileChargedQuestions ? <div className='display-center-row display-center-column align-full-center uploader-container' >
                                <p className='filename-tag'>{selectedFileQuestions.name}</p>
                                <Button style={{ width: 'auto' }} type="primary" danger onClick={handleChangeFileQuestions} icon={<SyncOutlined />}>Cambiar archivo</Button>
                            </div> : <div className='display-center-row display-center-column uploader-container'>
                                <FileUploaderAuto mostrarData={handleFileUploadQuestions} multi={false} openSwal={showSwal} uploadFile={true} tamanoArchivo={100} />
                            </div>
                        }
                    </div>
                    <Button style={{ width: 'auto', marginBottom: 25 }} type="primary" onClick={uploadFileQuestions} disabled={!fileChargedQuestions} icon={<UploadOutlined />}>Cargar preguntas</Button>
                </div>
                <Text style={{ display: 'block', marginBottom: 4 }}>
                    Para agregar una pregunta manualmente, haga click en "Agregar pregunta". Esto añadirá la pregunta a la tabla que se encuentra debajo del botón, donde deberá ingresar el título y el texto de la pregunta.
                </Text>
                <Text style={{ display: 'block', marginBottom: 4 }}>
                    Asigne además el grupo de respuesta que corresponde a cada pregunta. El grupo de respuesta es el conjunto de respuestas que se le presentan al evaluador para calificar al evaluado.
                </Text>
                <div className='display-center-row display-center-column align-full-center'>
                    <Button type="primary" onClick={handleAddQuestion} icon={<PlusOutlined />} style={{ marginBottom: 16, marginTop: 8 }}>
                        Agregar pregunta
                    </Button>
                    {data.evl_questions && data.evl_questions.length > 0 && (
                        <Button danger onClick={() => openMassDeleteDialog('questions')} icon={<DeleteOutlined />} style={{ marginBottom: 16, marginTop: 8, marginLeft: 8 }}>
                            Eliminar todas las preguntas ({data.evl_questions.length})
                        </Button>
                    )}
                </div>
                <TableEvaluationQuestions evlQuestions={data.evl_questions} dataEvaluationAnswerGroups={dataEvaluationAnswerGroups} handleSelectChange={handleQuestionChange} handleDeleteQuestion={handleDeleteQuestion} />
            </div>

            <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
                <Button onClick={onCancel}>Cancelar</Button>
                <Button type="primary" onClick={() => validateBeforeSave() && onSave()}>
                    {data.evl_id ? "Actualizar evaluación" : "Crear evaluación"}
                </Button>
            </Space>

            <Modal
                title={
                    massDeleteTarget === 'questions'
                        ? `Eliminar todas las preguntas (${(data.evl_questions || []).length})`
                        : `Eliminar todas las relaciones (${data.evl_relationships_loaded === false ? (relationshipsPagination.total || 0) : ((data.evl_relationships || []).length)})`
                }
                open={massDeleteTarget !== null}
                onCancel={closeMassDeleteDialog}
                onOk={handleConfirmMassDelete}
                okText="Confirmar eliminación"
                cancelText="Cancelar"
                okButtonProps={{ danger: true, disabled: massDeleteConfirmCode !== massDeleteCode }}
                destroyOnClose
            >
                <Text style={{ display: 'block', marginBottom: 12 }}>
                    Esta acción eliminará {massDeleteTarget === 'questions'
                        ? `todas las ${(data.evl_questions || []).length} pregunta(s) adicionales`
                        : `todas las ${data.evl_relationships_loaded === false ? (relationshipsPagination.total || 0) : ((data.evl_relationships || []).length)} relación(es) evaluador-evaluado`
                    } de esta evaluación. Esta acción no se puede deshacer.
                </Text>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    Escriba <Tag color="red">{massDeleteCode}</Tag> para confirmar:
                </Text>
                <Input
                    value={massDeleteConfirmCode}
                    onChange={(e) => setMassDeleteConfirmCode(e.target.value)}
                    placeholder={`Escriba ${massDeleteCode}`}
                    onPressEnter={handleConfirmMassDelete}
                />
            </Modal>

            <Modal
                title="Enviar correo de prueba"
                open={showTestEmailModal}
                onCancel={() => setShowTestEmailModal(false)}
                onOk={handleSendTestEmailPreview}
                confirmLoading={sendingTestEmail}
                okButtonProps={{ disabled: !testEmailPreviewHtml || loadingTestEmailPreview || Boolean(testEmailPreviewError) }}
                okText="Enviar prueba"
                cancelText="Cancelar"
                destroyOnClose={false}
                width={1080}
            >
                <Text style={{ display: 'block', marginBottom: 12 }}>
                    Se enviará un correo usando la información actualmente cargada en el formulario. Si la evaluación aún no ha sido guardada, el correo se enviará sin enlace activo.
                </Text>
                <Row gutter={12} style={{ marginBottom: 16 }}>
                    <Col xs={24} md={12}>
                        <Input
                            value={testEmailRecipientName}
                            onChange={(e) => setTestEmailRecipientName(e.target.value)}
                            placeholder="Nombre del destinatario"
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <Input
                            value={testEmailRecipient}
                            onChange={(e) => setTestEmailRecipient(e.target.value)}
                            placeholder="Correo destino para la prueba"
                        />
                    </Col>
                </Row>
                <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}>
                    <Text type="secondary">
                        La vista previa se actualiza automáticamente con los datos actuales del formulario.
                    </Text>
                    <Space wrap>
                        <Button onClick={() => fetchTestEmailPreview({ notifyOnError: true })} loading={loadingTestEmailPreview}>
                            Actualizar previsualización
                        </Button>
                        <Button onClick={handleOpenPreviewInNewTab} disabled={!testEmailPreviewHtml || loadingTestEmailPreview}>
                            Abrir en nueva pestaña
                        </Button>
                    </Space>
                </Space>
                {testEmailPreviewLinkActive ? (
                    <Alert
                        type="success"
                        showIcon
                        message="La vista previa incluye enlace activo para esta evaluación."
                        style={{ marginBottom: 12 }}
                    />
                ) : (
                    <Alert
                        type="info"
                        showIcon
                        message="La vista previa se genera sin enlace activo mientras la evaluación no tenga un identificador guardado."
                        style={{ marginBottom: 12 }}
                    />
                )}
                <div className="evaluation-email-preview-shell">
                    {loadingTestEmailPreview ? (
                        <div className="evaluation-email-preview-state">
                            <Spin />
                            <Text type="secondary">Generando previsualización del correo...</Text>
                        </div>
                    ) : testEmailPreviewError ? (
                        <Alert type="warning" showIcon message={testEmailPreviewError} />
                    ) : testEmailPreviewHtml ? (
                        <iframe
                            title="Vista previa del correo"
                            srcDoc={testEmailPreviewHtml}
                            className="evaluation-email-preview-frame"
                        />
                    ) : (
                        <div className="evaluation-email-preview-state">
                            <Text type="secondary">Complete los datos del destinatario para ver la previsualización.</Text>
                        </div>
                    )}
                </div>
            </Modal>
        </Space>
    );
};

export default FormEvaluation;
